import React, { useState, useEffect } from 'react';
import { Search, ChevronRight, User } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { getCustomers, getPets, createAppointment } from '../../services/api';
import { toast } from 'sonner';
import PetIcon from '../PetIcon';

const TYPES = [
  { v: 'general', l: 'Genel' },
  { v: 'vaccine', l: 'Aşı' },
  { v: 'treatment', l: 'Tedavi' },
  { v: 'checkup', l: 'Kontrol' },
];

const NewAppointmentModal = ({ open, onClose, onCreated, defaultDate }) => {
  const [step, setStep] = useState(1);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [pets, setPets] = useState([]);
  const [form, setForm] = useState({
    pet_id: '', date: '', time: '', note: '', type: 'general'
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (open) {
      setStep(1);
      setQuery('');
      setResults([]);
      setSelectedCustomer(null);
      setPets([]);
      setForm({ pet_id: '', date: defaultDate || '', time: '', note: '', type: 'general' });
    }
  }, [open, defaultDate]);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const r = await getCustomers(query); setResults(r.data); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const selectCustomer = async (c) => {
    setSelectedCustomer(c);
    try {
      const r = await getPets(c.id);
      setPets(r.data);
      if (r.data.length === 1) set('pet_id', r.data[0].id);
    } catch {}
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!form.date) { toast.error('Tarih gerekli'); return; }
    setSaving(true);
    try {
      await createAppointment({
        customer_id: selectedCustomer.id,
        pet_id: form.pet_id || null,
        date: form.date,
        time: form.time || null,
        note: form.note.trim() || null,
        type: form.type,
      });
      toast.success('Randevu oluşturuldu');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Randevu oluşturulamadı');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="new-appointment-modal" className="sm:rounded-2xl sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden flex flex-col p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 2 && (
              <button onClick={() => setStep(1)} className="text-xs text-[#6b6de3] font-medium hover:underline mr-1">
                Geri
              </button>
            )}
            {step === 1 ? 'Müşteri Seç' : 'Yeni Randevu'}
            <span className="ml-auto text-xs text-[#8E8E93] font-normal">{step}/2</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-1 max-h-[50vh] overflow-y-auto">
          {step === 1 && (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={16} />
                <Input type="search" placeholder="Müşteri Ara (İsim, telefon veya TCKN)" value={query}
                  onChange={e => setQuery(e.target.value)}
                  className="pl-9 h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl" autoFocus data-testid="apt-customer-search" />
              </div>
              {results.length === 0 && query.length >= 2 && (
                <p className="text-center text-sm text-[#8E8E93] py-4">Müşteri bulunamadı</p>
              )}
              {results.map(c => (
                <button key={c.id} onClick={() => selectCustomer(c)}
                  className="w-full flex items-center justify-between px-3 py-2.5 bg-[#F2F2F7] rounded-xl hover:bg-gray-200 text-left"
                  data-testid={`apt-customer-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{c.name}</p>
                    <p className="text-xs text-[#8E8E93]">{c.phone}</p>
                    {c.pets && c.pets.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {c.pets.map(p => (
                          <span key={p.id} className="inline-flex items-center gap-0.5 text-[10px] bg-white px-1.5 py-0.5 rounded-md text-gray-600">
                            <PetIcon species={p.species} size={10} /> {p.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <ChevronRight size={18} className="text-[#C7C7CC] shrink-0 ml-2" />
                </button>
              ))}
            </>
          )}

          {step === 2 && (
            <>
              <div className="bg-blue-50 rounded-xl p-3 flex items-center gap-2">
                <User size={16} className="text-[#6b6de3]" />
                <span className="text-sm font-medium text-gray-900">{selectedCustomer?.name}</span>
                <span className="text-xs text-[#8E8E93]">({selectedCustomer?.phone})</span>
              </div>

              {pets.length > 0 && (
                <div>
                  <Label className="text-xs">Hasta</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {pets.map(p => (
                      <button key={p.id} onClick={() => set('pet_id', form.pet_id === p.id ? '' : p.id)}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${
                          form.pet_id === p.id ? 'bg-[#6b6de3] text-white' : 'bg-[#F2F2F7] text-gray-700'
                        }`} data-testid={`apt-pet-${p.id}`}>
                        <PetIcon species={p.species} size={12} /> {p.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs">Randevu Türü</Label>
                <div className="flex gap-1.5 mt-1">
                  {TYPES.map(t => (
                    <button key={t.v} onClick={() => set('type', t.v)}
                      className={`flex-1 py-2 rounded-xl text-xs font-medium transition-colors ${
                        form.type === t.v ? 'bg-[#6b6de3] text-white' : 'bg-[#F2F2F7] text-gray-700'
                      }`} data-testid={`apt-type-${t.v}`}>{t.l}</button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Tarih *</Label>
                  <Input type="date" value={form.date} onChange={e => set('date', e.target.value)}
                    className="h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl mt-1" data-testid="apt-date" />
                </div>
                <div>
                  <Label className="text-xs">Saat</Label>
                  <Input type="time" value={form.time} onChange={e => set('time', e.target.value)}
                    className="h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl mt-1" data-testid="apt-time" />
                </div>
              </div>

              <div>
                <Label className="text-xs">Not</Label>
                <Input placeholder="Opsiyonel not..." value={form.note} onChange={e => set('note', e.target.value)}
                  className="h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl mt-1" data-testid="apt-note" />
              </div>

              <Button onClick={handleSubmit} disabled={saving}
                className="w-full h-11 bg-[#6b6de3] hover:bg-[#5658c4] rounded-xl text-sm font-semibold"
                data-testid="save-appointment-btn">
                {saving ? 'Kaydediliyor...' : 'Randevu Oluştur'}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewAppointmentModal;
